import React, {useState, useRef, useEffect, useCallback, useMemo} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useLanguage} from '../../../_gori/i18n/Metronici18n'
import LanguageToggle from '../../modules/common/components/LanguageToggle'
import DemoPanel, {DemoPanelVideo} from '../../modules/demo/DemoPanel'
import {SeoHead} from '../../modules/seo'
import {
  DEMO_ALL_SUBTOPICS,
  DEMO_SUBTOPIC_INDEX,
  DEMO_TOPICS,
  buildDemoVideoUrl,
} from '../../modules/demo/_demoConfig'

interface LocationState {
  videos: DemoPanelVideo[]
  totalDuration: string
  selectedSubtopicKeys?: string[]
  startVideoIndex?: number
}

const DemoPlayerPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const intl = useIntl()
  const {language: currentLanguage} = useLanguage()
  const videoRef = useRef<HTMLVideoElement>(null)

  // Map UI language to S3 video language code (matches DemoTopicsPage logic).
  const language = useMemo(() => {
    if (currentLanguage.startsWith('ja')) return 'ja'
    if (currentLanguage.startsWith('zh')) return 'zh'
    return 'en'
  }, [currentLanguage])

  const state = location.state as LocationState
  const originalVideos = useMemo(() => state?.videos || [], [state?.videos])
  const totalDuration = state?.totalDuration || '0 min'
  const selectedSubtopicKeys = useMemo(
    () => state?.selectedSubtopicKeys || originalVideos.map((v) => v.key),
    [state?.selectedSubtopicKeys, originalVideos]
  )
  const startVideoIndex = state?.startVideoIndex || 0

  // Re-translate titles + re-resolve URLs whenever the language changes.
  // Built off the `key` in each video, which is the i18n message id.
  const videos: DemoPanelVideo[] = useMemo(
    () =>
      originalVideos.map((video) => {
        const entry = DEMO_SUBTOPIC_INDEX[video.key]
        return {
          ...video,
          title: intl.formatMessage({id: video.key}),
          url: entry ? buildDemoVideoUrl(entry.subtopic.videoPath, language) : video.url,
        }
      }),
    [originalVideos, language, intl]
  )

  const unwatchedKeys = useMemo(
    () => DEMO_ALL_SUBTOPICS.filter((s) => !selectedSubtopicKeys.includes(s.key)).map((s) => s.key),
    [selectedSubtopicKeys]
  )
  const allWatched = unwatchedKeys.length === 0
  // Modal lists either skipped subtopics, or all of them once everything has been watched.
  const modalKeys = allWatched ? DEMO_ALL_SUBTOPICS.map((s) => s.key) : unwatchedKeys

  const [currentVideoIndex, setCurrentVideoIndex] = useState(startVideoIndex)
  const [isPanelMinimized, setIsPanelMinimized] = useState(false)
  const [videoAspectRatio, setVideoAspectRatio] = useState('906/532')
  const [showFinishModal, setShowFinishModal] = useState(false)
  // Selected subtopic keys for the "add more topics" CTA.
  const [selectedModalKeys, setSelectedModalKeys] = useState<string[]>([])
  // Collapse state for topic cards in the finish modal (all collapsed by default).
  const [expandedModalTopics, setExpandedModalTopics] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(DEMO_TOPICS.map((t) => [t.key, false]))
  )

  const currentVideo = videos[currentVideoIndex]

  const handleVideoEnd = useCallback(() => {
    if (currentVideoIndex < videos.length - 1) {
      setCurrentVideoIndex((idx) => idx + 1)
    } else {
      setShowFinishModal(true)
    }
  }, [currentVideoIndex, videos.length])

  const handleVideoSelect = (index: number) => {
    setCurrentVideoIndex(index)
  }

  const handlePlayFirstVideo = () => {
    if (videos.length > 0) {
      setCurrentVideoIndex(0)
      if (videoRef.current) {
        videoRef.current.src = videos[0].url
        videoRef.current.load()
        videoRef.current.play()
      }
    }
  }

  // Reload when current video changes (index or language switch).
  useEffect(() => {
    if (videoRef.current && currentVideo) {
      videoRef.current.src = currentVideo.url
      videoRef.current.load()
    }
  }, [currentVideo])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleMetadata = () => {
      setVideoAspectRatio(`${video.videoWidth}/${video.videoHeight}`)
    }

    video.addEventListener('ended', handleVideoEnd)
    video.addEventListener('loadedmetadata', handleMetadata)

    return () => {
      video.removeEventListener('ended', handleVideoEnd)
      video.removeEventListener('loadedmetadata', handleMetadata)
    }
  }, [handleVideoEnd])

  if (!videos.length) {
    return <div>{intl.formatMessage({id: 'DEMO_PLAYER_NO_VIDEOS'})}</div>
  }

  return (
    <div className='demo-container'>
      <SeoHead />
      <div className='demo-video-area'>
        <div className='demo-video-container'>
          <video
            ref={videoRef}
            className='demo-video'
            style={{aspectRatio: videoAspectRatio}}
            controls
            onError={() => {
              const el = videoRef.current
              if (!el || !currentVideo) return
              const enUrl = buildDemoVideoUrl(
                DEMO_SUBTOPIC_INDEX[currentVideo.key]?.subtopic.videoPath ?? '',
                'en'
              )
              // Only fallback once — if already on the en URL, don't loop.
              if (el.src !== enUrl && enUrl) {
                el.src = enUrl
                el.load()
              }
            }}
          />
        </div>

        <div className='demo-back-button'>
          <button
            onClick={() => navigate('/demo/topics')}
            className='btn d-flex align-items-center gap-1 demo-back-button-link'
          >
            {intl.formatMessage({id: 'DEMO_BACK_BUTTON'})}
          </button>
        </div>
      </div>

      <DemoPanel
        key={`player-${language}`}
        mode='player'
        totalDuration={totalDuration}
        videosCount={videos.length}
        videos={videos}
        currentVideoIndex={currentVideoIndex}
        isPanelMinimized={isPanelMinimized}
        onVideoSelect={handleVideoSelect}
        onPlayFirstVideo={handlePlayFirstVideo}
        onCollapsePanel={() => setIsPanelMinimized(!isPanelMinimized)}
      />

      <LanguageToggle allowedCodes={['en', 'zh']} />

      {showFinishModal && (
        <div
          key={language}
          className='modal d-block demo-finish-modal'
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h4 className='modal-title'>{intl.formatMessage({id: 'DEMO_MODAL_TITLE'})}</h4>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => setShowFinishModal(false)}
                />
              </div>
              <div className='modal-body'>
                <p className='modal-subtitle mb-3'>
                  {intl.formatMessage({
                    id: allWatched ? 'DEMO_MODAL_SUBTITLE_REVISIT' : 'DEMO_MODAL_SUBTITLE_SKIPPED',
                  })}
                </p>

                <div className='mb-4 demo-modal-list'>
                  {DEMO_TOPICS.map((topic) => {
                    // Filter to only show subtopics that are in modalKeys
                    const topicModalSubtopics = topic.subtopics.filter((s) =>
                      modalKeys.includes(s.key)
                    )
                    if (topicModalSubtopics.length === 0) return null

                    const isExpanded = expandedModalTopics[topic.key]
                    const topicSubtopicKeys = topicModalSubtopics.map((s) => s.key)
                    const isTopicSelected = topicSubtopicKeys.every((k) =>
                      selectedModalKeys.includes(k)
                    )

                    return (
                      <div className='demo-modal-card' key={topic.key}>
                        {/* Topic header */}
                        <div className='d-flex align-items-center justify-content-between'>
                          <div className='d-flex align-items-center flex-grow-1'>
                            <div
                              className={`demo-checkbox ${isTopicSelected ? 'checked' : ''}`}
                              onClick={() => {
                                // Toggle all subtopics in this topic
                                if (isTopicSelected) {
                                  setSelectedModalKeys((prev) =>
                                    prev.filter((k) => !topicSubtopicKeys.includes(k))
                                  )
                                } else {
                                  setSelectedModalKeys((prev) => [
                                    ...prev,
                                    ...topicSubtopicKeys.filter((k) => !prev.includes(k)),
                                  ])
                                }
                              }}
                            />
                            <span className='demo-main-topic-text'>
                              {intl.formatMessage({id: topic.key})}
                            </span>
                          </div>
                          <span className='demo-subtopics-count me-3'>
                            {intl.formatMessage(
                              {id: 'DEMO_SUB_TOPICS_COUNT'},
                              {count: topicModalSubtopics.length}
                            )}
                          </span>
                          <button
                            type='button'
                            className='demo-topic-toggle-btn'
                            onClick={(e) => {
                              e.stopPropagation()
                              setExpandedModalTopics((prev) => ({
                                ...prev,
                                [topic.key]: !prev[topic.key],
                              }))
                            }}
                            aria-expanded={isExpanded}
                            aria-label={intl.formatMessage({
                              id: isExpanded
                                ? 'DEMO_TOPIC_TOGGLE_COLLAPSE'
                                : 'DEMO_TOPIC_TOGGLE_EXPAND',
                            })}
                          >
                            {isExpanded ? (
                              <svg
                                width='16'
                                height='16'
                                viewBox='0 0 16 16'
                                fill='none'
                                aria-hidden='true'
                              >
                                <rect x='2' y='7' width='12' height='2' rx='1' fill='#475569' />
                              </svg>
                            ) : (
                              <svg
                                width='16'
                                height='16'
                                viewBox='0 0 16 16'
                                fill='none'
                                aria-hidden='true'
                              >
                                <rect x='2' y='7' width='12' height='2' rx='1' fill='#475569' />
                                <rect x='7' y='2' width='2' height='12' rx='1' fill='#475569' />
                              </svg>
                            )}
                          </button>
                        </div>

                        {/* Subtopics — only when expanded */}
                        {isExpanded && (
                          <div className='demo-topic-subtopics-section'>
                            <div className='d-flex align-items-start mt-3'>
                              <img
                                src='/media/gori/demo/subtopic-arrow.svg'
                                alt=''
                                aria-hidden='true'
                                className='demo-subtopic-arrow'
                              />
                              <div className='flex-grow-1'>
                                <div className='demo-subtopics-label mb-2'>
                                  {intl.formatMessage({id: 'DEMO_TOPICS_SUB_TOPICS_LABEL'})}
                                </div>
                                <div className='demo-subtopics-grid demo-subtopics-grid--stacked'>
                                  {topicModalSubtopics.map((subtopic) => {
                                    const isSelected = selectedModalKeys.includes(subtopic.key)
                                    return (
                                      <div
                                        key={subtopic.key}
                                        className='demo-subtopic-container'
                                        onClick={() => {
                                          setSelectedModalKeys((prev) =>
                                            prev.includes(subtopic.key)
                                              ? prev.filter((k) => k !== subtopic.key)
                                              : [...prev, subtopic.key]
                                          )
                                        }}
                                      >
                                        <div
                                          className={`demo-checkbox-small ${
                                            isSelected ? 'checked' : ''
                                          }`}
                                        />
                                        <span className='demo-subtopic-text'>
                                          {intl.formatMessage({id: subtopic.key})}
                                        </span>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-secondary'
                  onClick={() => navigate('/demo')}
                >
                  {intl.formatMessage({id: 'DEMO_MODAL_NO_THANKS'})}
                </button>
                <button
                  type='button'
                  className='btn btn-advanced-primary'
                  disabled={selectedModalKeys.length === 0}
                  onClick={() => {
                    const newlyAddedSubtopicKeys = selectedModalKeys
                    navigate('/demo/topics', {
                      state: {
                        preSelectedSubtopicKeys: allWatched
                          ? newlyAddedSubtopicKeys
                          : [...selectedSubtopicKeys, ...newlyAddedSubtopicKeys],
                        autoStart: true,
                        newlyAddedSubtopicKeys,
                      },
                    })
                  }}
                >
                  {intl.formatMessage({id: 'DEMO_MODAL_ADD_TO_DEMO'})}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DemoPlayerPage
